import {
  createResourceInfiniteQueryAtom,
  createResourceQueryAtom,
  defineResource,
  defineResourceInfiniteQuery,
  defineResourceQuery,
} from "#/shared/lib/resource";

import { getConversationMessages } from "../api/get-conversation-messages";
import { getConversations } from "../api/get-conversations";

const conversationsResource = defineResource("conversations");

/** 列表与详情每 30 秒刷新新消息和发送结果，回到前台立即重取。 */
const listQuery = {
  refetchInterval: 30_000,
  refetchOnWindowFocus: "always",
  staleTime: 30_000,
} as const;

const conversationListQuery = defineResourceInfiniteQuery({
  ...listQuery,
  key: () => [],
  name: "list",
  queryFn: (_input: undefined, cursor) => getConversations(cursor),
  resource: conversationsResource,
});

/**
 * Inbox 列表读模型：一页一次请求，页与页在 defineResourceInfiniteQuery 里拼起来，
 * 翻到 nextCursor 为 null 就是底。
 */
export const conversationListAtom = createResourceInfiniteQueryAtom(
  conversationListQuery,
  () => undefined,
);

const conversationMessagesQuery = defineResourceQuery({
  key: (conversationId: string) => [conversationId],
  name: "messages",
  queryFn: (conversationId: string) => getConversationMessages(conversationId),
  refetchOnWindowFocus: "always",
  resource: conversationsResource,
  // 刷新新消息及迟到的邮件发送结果。
  refetchInterval: 30_000,
  staleTime: 60_000,
});

const atomsByConversationId = new Map<
  string,
  ReturnType<typeof createConversationMessagesAtom>
>();

function createConversationMessagesAtom(conversationId: string) {
  return createResourceQueryAtom(
    conversationMessagesQuery,
    () => conversationId,
  );
}

/**
 * 单条会话的回放读模型。每看过一条会话就留下一个 atom，来回点不会重新拉取；
 * 会话数据本身由 react-query 缓存管理，这里留下的只是 atom 外壳。
 */
export function conversationMessagesAtom(conversationId: string) {
  const existing = atomsByConversationId.get(conversationId);

  if (existing !== undefined) {
    return existing;
  }

  const created = createConversationMessagesAtom(conversationId);
  atomsByConversationId.set(conversationId, created);

  return created;
}
